import { z } from "zod";
import {
  JOURNEY_STATE_ORDINAL,
  type JourneyState,
} from "./patient-journey-state";
import type { PrescriptionCycleState } from "./prescription-cycle";
import { measurementReviewStatusSchema } from "./measurement-review";
import { treatmentCommentSchema } from "./din-vard";
import { bloodTestStatusSchema } from "./medical-evidence";

// CAR-16 — patient weight log + body measurements. Wire shapes:
// `loggedFor` is YYYY-MM-DD (the calendar day the patient is logging
// for), `loggedAt` is the ISO timestamp the row was written.

// 'checkin' (TES-357) = ingested from the weekly check-in's optional weight
// field; 'f01' (TES-471) = the weight given in the F-01 qualification quiz
// (app or web lead). A deliberate quick-log ('patient') always wins the day
// over either.
export const weightLogSourceSchema = z.enum([
  "patient",
  "doctor",
  "webdoc",
  "checkin",
  "f01",
]);
export type WeightLogSource = z.infer<typeof weightLogSourceSchema>;

export const weightLogSchema = z.object({
  id: z.string().uuid(),
  kg: z.number(),
  loggedFor: z.string(),
  loggedAt: z.string(),
  source: weightLogSourceSchema,
});
export type WeightLog = z.infer<typeof weightLogSchema>;

export const measurementEntrySchema = z.object({
  id: z.string().uuid(),
  waistCm: z.number().nullable(),
  hipCm: z.number().nullable(),
  loggedFor: z.string(),
});
export type MeasurementEntry = z.infer<typeof measurementEntrySchema>;

// TES-1366 — systolic must be strictly greater than diastolic.
//
// Three of the first four real prod readings were entered upside down (80/120,
// 90/130, 91/140), from three different screens: each value sat inside its
// own bounds, so nothing noticed. A reading whose upper pressure is at or
// below the lower one is not a blood pressure; it is the two boxes swapped.
//
// One rule and one issue code, shared by every layer: the API's session and
// vital schemas, the form-answer schema, and the app's inline error. It
// applies to NEW input only. Stored rows are never re-validated through these
// schemas (the read shapes `vitalEntrySchema` and `measurementSchema` stay
// unrefined), so an existing inverted reading still loads and renders.
export const BLOOD_PRESSURE_ORDER_ISSUE = "systolic_not_above_diastolic";

export function isBloodPressureOrderValid(
  systolic: number,
  diastolic: number,
): boolean {
  return systolic > diastolic;
}

const bloodPressureOrderRefinement = {
  message: BLOOD_PRESSURE_ORDER_ISSUE,
  // The error sits on the upper value, the one read first.
  path: ["systolic"],
};

// The systolic/diastolic answer keys of the form templates that ask for a
// blood pressure: F-07 (prescription transfer) and F-05 (the monthly
// check-in, milestone v3). A list rather than a naming convention, like the
// attestation keys; the API's template constants are pinned to it by a test.
export const FORM_BLOOD_PRESSURE_FIELD_PAIRS: ReadonlyArray<{
  systolic: string;
  diastolic: string;
}> = [
  { systolic: "bp_systolic", diastolic: "bp_diastolic" },
  { systolic: "month_bp_systolic", diastolic: "month_bp_diastolic" },
];

/**
 * The form blood-pressure pairs that are both answered with numbers and are
 * inverted. `getAnswer` reads a dot-path, so the app and the API each pass
 * their own answer-tree reader.
 */
export function invertedFormBloodPressurePairs(
  getAnswer: (key: string) => unknown,
): Array<{ systolic: string; diastolic: string }> {
  return FORM_BLOOD_PRESSURE_FIELD_PAIRS.filter((pair) => {
    const s = getAnswer(pair.systolic);
    const d = getAnswer(pair.diastolic);
    return (
      typeof s === "number" &&
      typeof d === "number" &&
      !isBloodPressureOrderValid(s, d)
    );
  });
}

// TES-216 — a vital-sign reading (blood pressure). Realistic mmHg bounds reject
// typos without shutting out outliers.
export const logVitalSchema = z
  .object({
    systolic: z.number().int().min(50).max(300),
    diastolic: z.number().int().min(30).max(200),
    loggedFor: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .refine(
    (v) => isBloodPressureOrderValid(v.systolic, v.diastolic),
    bloodPressureOrderRefinement,
  );
export type LogVital = z.infer<typeof logVitalSchema>;

export const vitalEntrySchema = z.object({
  id: z.string().uuid(),
  systolic: z.number().int(),
  diastolic: z.number().int(),
  loggedFor: z.string(),
});
export type VitalEntry = z.infer<typeof vitalEntrySchema>;

// ---------------------------------------------------------------------------
// Health v3 — the shared measurement source (`measurement_dictionary.csv`).
//
// Wire shapes for `patient_measurements`. Distinct from the CAR-16 shapes above,
// which stay until each legacy read path is migrated deliberately.

// BMI is absent on purpose: the dictionary marks it derived_from_height_and_weight,
// so it is computed for display and never stored or posted.
export const measurementTypeSchema = z.enum([
  "height",
  "weight",
  "waist",
  "hip",
  "blood_pressure",
  "pulse",
]);
export type MeasurementType = z.infer<typeof measurementTypeSchema>;

export const measurementSourceSchema = z.enum([
  "patient",
  "form",
  "clinician",
  "import",
]);
export type MeasurementSource = z.infer<typeof measurementSourceSchema>;

// Per-type bounds. These reject typos (a 9 kg adult, a 400 cm waist) without
// excluding real outliers, and they are the same numbers the DB CHECK protects
// structurally — the constraint guards shape, this guards plausibility.
export const MEASUREMENT_BOUNDS: Record<
  Exclude<MeasurementType, "blood_pressure">,
  { min: number; max: number; unit: string }
> = {
  height: { min: 100, max: 250, unit: "cm" },
  weight: { min: 20, max: 500, unit: "kg" },
  waist: { min: 40, max: 250, unit: "cm" },
  hip: { min: 40, max: 250, unit: "cm" },
  pulse: { min: 25, max: 220, unit: "bpm" },
};

// Health v3 §1 (decision 2026-08-31) — the dated occasion is the object.
//
// Blood pressure and pulse belong to ONE MeasurementSession. Form answers and
// the 7-day rule link to `measurement_session_id`, never to a single component
// row, so "which reading was this" survives a correction.
//
// measuredAt, source, sourceSurface and supersession live on the SESSION.
// Components keep only their own value and validation — that is the whole
// point: two components of one occasion can no longer carry different dates.
const scalarComponent = <T extends Exclude<MeasurementType, "blood_pressure">>(
  type: T,
) =>
  z
    .number()
    .min(MEASUREMENT_BOUNDS[type].min)
    .max(MEASUREMENT_BOUNDS[type].max)
    .optional();

// Systolic and diastolic are ONE component. Nesting them makes "must appear as
// a pair if blood pressure is given" structural rather than a refinement that a
// later edit can forget.
export const bloodPressureComponentSchema = z
  .object({
    systolic: z.number().int().min(50).max(300),
    diastolic: z.number().int().min(30).max(200),
  })
  .refine(
    (v) => isBloodPressureOrderValid(v.systolic, v.diastolic),
    bloodPressureOrderRefinement,
  );

export const measurementComponentsSchema = z.object({
  height: scalarComponent("height"),
  weight: scalarComponent("weight"),
  waist: scalarComponent("waist"),
  hip: scalarComponent("hip"),
  // Pulse is a component of the same session and may be given WITHOUT blood
  // pressure — the decision is explicit about that.
  pulse: scalarComponent("pulse"),
  bloodPressure: bloodPressureComponentSchema.optional(),
});
export type MeasurementComponents = z.infer<typeof measurementComponentsSchema>;

export const logMeasurementSessionSchema = z.object({
  measuredAt: z.string().datetime().optional(),
  // Where the patient entered it ("health/trends", "F-02"). Belongs to the
  // occasion, not to any one value.
  sourceSurface: z.string().min(1).max(120).optional(),
  // Correcting an earlier occasion rather than adding a new one. Supersession
  // is session-level: correcting a blood pressure supersedes the pulse taken
  // with it, instead of leaving half the reading standing.
  supersedesSessionId: z.string().uuid().optional(),
  components: measurementComponentsSchema,
});
export type LogMeasurementSession = z.infer<typeof logMeasurementSessionSchema>;

// TES-863 (Ö-211) — the clinician's correction of a measurement occasion.
//
// Emelie 2026-09-22, translated: the patient's original form answers are
// immutable originals; Kirill corrects the structured, current medical
// profile through a corrected version next to the original. Measurements are
// the group where that model was already built — supersession at session
// level, with a `clinician` source the schema has always allowed — and the
// only thing missing was a door for a doctor to walk through.
//
// Two differences from the patient's own logging, and both are Ö-211's
// mandatory content rather than our additions:
//
//   - `supersedesSessionId` is REQUIRED. A clinician adding a fresh reading is
//     a different act from correcting one, and this endpoint is only the
//     second. Making it optional would quietly turn "rätta" into "lägg till".
//   - `reason` is REQUIRED. "Ändringsorsak" is listed among the things a
//     correction must carry, and a correction whose reason is blank is the
//     thing the audit trail exists to prevent.
//
// Free text, not a taxonomy: Ö-182 got a structured list because Emelie wrote
// one, and there is none here. A list we invented would look structured while
// carrying less than a sentence from the doctor who made the correction.
export const correctMeasurementSessionSchema = z.object({
  measuredAt: z.string().datetime().optional(),
  supersedesSessionId: z.string().uuid(),
  reason: z.string().trim().min(1).max(2000),
  components: measurementComponentsSchema,
});
export type CorrectMeasurementSession = z.infer<
  typeof correctMeasurementSessionSchema
>;

// TES-1034 (Emelie's point 7) — a verified blood pressure / resting pulse the
// doctor actually has, recorded as a NEW occasion of his own.
//
// Kirill may no longer overwrite her self-reported blood pressure or pulse
// (the correction door refuses it). What he may do is ask her to correct it,
// or document a separate measurement he has a real basis for. This is the
// second: it never supersedes anything, so her original stays exactly as she
// entered it, and `basis` (where the value comes from) is required, because a
// "verified" value without a stated source is a correction by another name.
export const recordVerifiedMeasurementSchema = z
  .object({
    measuredAt: z.string().datetime(),
    basis: z.string().trim().min(1).max(2000),
    components: z
      .object({
        pulse: scalarComponent("pulse"),
        bloodPressure: bloodPressureComponentSchema.optional(),
      })
      .strict(),
  })
  .strict();
export type RecordVerifiedMeasurement = z.infer<
  typeof recordVerifiedMeasurementSchema
>;

/**
 * Every component is optional — "samtliga komponenter är frivilliga" — but a
 * session with none is not a measurement, it is an empty row. Callers validate
 * with this rather than a `.refine` so the error is theirs to phrase.
 */
export function hasAnyComponent(c: MeasurementComponents): boolean {
  return (
    c.height !== undefined ||
    c.weight !== undefined ||
    c.waist !== undefined ||
    c.hip !== undefined ||
    c.pulse !== undefined ||
    c.bloodPressure !== undefined
  );
}

// One stored component. measuredAt/source/current are PROJECTED from the
// session at read time — they are not stored here, so they cannot disagree
// with the occasion they belong to.
export const measurementSchema = z.object({
  id: z.string().uuid(),
  type: measurementTypeSchema,
  value: z.number().nullable(),
  systolic: z.number().int().nullable(),
  diastolic: z.number().int().nullable(),
  unit: z.string(),
  sessionId: z.string(),
  measuredAt: z.string(),
  source: measurementSourceSchema,
  // True when nothing supersedes this component's SESSION — i.e. the value
  // that counts. "Gäller nu" in the patient-facing version history.
  current: z.boolean(),
  // TES-999 (Ö-239) — what a correction's history needs beyond `current`:
  // the occasion this one replaced, and when it was recorded. For a
  // correction `recordedAt` is the correction time ("rättelsetid"), which
  // `measuredAt` cannot say — a correction keeps the occasion's own date.
  // Optional so a client built before them (app build 332) is unaffected.
  supersedesSessionId: z.string().nullable().optional(),
  recordedAt: z.string().optional(),
  // TES-1255 — where the occasion was entered (the session's free-text
  // sourceSurface: "health/trends", "F-05", "cp06/blood_pressure_pulse", …),
  // which "Din hälsa över tid" shows on every point through
  // measurementOrigin. Optional so earlier builds are unaffected.
  sourceSurface: z.string().nullable().optional(),
  // TES-1456 (Ö-753) — on her own list, for a current weight: whether it is
  // one she reported herself and so may correct with "Rätta registrering"
  // (isSelfReportedOccasion). The server refuses the others. Optional so
  // earlier builds (2.0.11) are unaffected; they keep offering every weight
  // and are refused on save for one that is not hers.
  patientCorrectable: z.boolean().optional(),
  // TES-1056 — the doctor's assessment of the occasion. Only the doctor's
  // list carries it; the patient's own reads leave it out.
  reviewStatus: measurementReviewStatusSchema.optional(),
});
export type Measurement = z.infer<typeof measurementSchema>;

// TES-999 (Ö-239) — the patient's correction of ONE weight entry
// ("Rätta registrering" in the weight graph).
//
// Emelie 2026-09-29:
// "Patienten ska däremot kunna rätta en enskild felregistrering via Rätta registrering i viktgrafen. Tidigare värde, nytt värde och rättelsetid ska bevaras i historiken."
//
// The body names the weight VALUE she tapped, not the session, and carries
// only the new weight. The server rebuilds the rest of the occasion from what
// is stored, so correcting a weight can never quietly change a height or a
// blood pressure that was recorded with it.
//
// TES-1015 — or it names a weight LOG. Home's "Vikt idag" and the weekly
// check-in write only the legacy weight log, and that is the everyday way she
// logs her weight. Exactly one of the two ids.
const correctionKgSchema = z
  .number()
  .min(MEASUREMENT_BOUNDS.weight.min)
  .max(MEASUREMENT_BOUNDS.weight.max);
// TES-1357 — and, optionally, the day the reading was actually taken. 09B:
// "Patienten kan korrigera vikt och mätdatum för en vikt hon själv har rapporterat"
// A calendar day in the tenant zone (YYYY-MM-DD), the same day `weightDayOf`
// gives a reading; the server refuses a day after today. Optional because app
// 2.0.9 sends the weight only, and a correction without it keeps the date.
const correctionDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (day) => {
      const d = new Date(`${day}T00:00:00.000Z`);
      return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === day;
    },
    { message: "not a calendar day" },
  );
// TES-1605 — and, optionally, the surface she corrected it from, when that
// is Home's "Ändra vikt" rather than "Rätta registrering" in "Din hälsa över
// tid". Only "hem": a correction without it (every app before TES-1605, and
// "Din hälsa över tid") keeps the correction surface.
export const WEIGHT_CORRECTION_SURFACES = ["hem"] as const;
const correctionSurfaceSchema = z.enum(WEIGHT_CORRECTION_SURFACES);
export const correctOwnWeightSchema = z.union([
  z
    .object({
      measurementId: z.string().uuid(),
      kg: correctionKgSchema,
      measuredOn: correctionDaySchema.optional(),
      sourceSurface: correctionSurfaceSchema.optional(),
    })
    .strict(),
  z
    .object({
      weightLogId: z.string().uuid(),
      kg: correctionKgSchema,
      measuredOn: correctionDaySchema.optional(),
      sourceSurface: correctionSurfaceSchema.optional(),
    })
    .strict(),
]);
export type CorrectOwnWeight = z.infer<typeof correctOwnWeightSchema>;

// TES-1015 — the weight-log sources she reported herself, and so may correct
// herself. `doctor` and `webdoc` are not hers.
export const PATIENT_CORRECTABLE_WEIGHT_LOG_SOURCES: readonly WeightLogSource[] =
  ["patient", "checkin", "f01"];

/**
 * TES-1456 (Emelie 2026-10-19, Ö-753) — whether an occasion in the
 * measurement series is one she reported herself, by its real origin:
 * "Patienten får endast rätta egna självrapporterade vikter." "Import bedöms efter verkligt ursprung."
 *
 * - `patient`: Home's "Vikt idag", "Dina värden", her own corrections.
 * - `form`: her answers in F-01, F-02/F-02B, F-04, F-05, F-07 and the
 *   kompletteringar ("Formulärnamn … får inte i sig omklassificera").
 * - `import` (the TES-1249 backfill): the legacy row it was copied from,
 *   recorded in `provenance.weightLogSource` — hers for a quick-log, a
 *   check-in or F-01, not for `doctor` or `webdoc`. An import without one
 *   (a CAR-16 waist/hip row) has no origin we can show to be hers.
 * - `clinician`: not hers.
 *
 * The server also follows a doctor's correction of an occasion back to her
 * weight when he carried it over unchanged (MeasurementService.weightIsHers).
 */
export function isSelfReportedOccasion(session: {
  source: string;
  provenance?: unknown;
}): boolean {
  if (session.source === "patient" || session.source === "form") return true;
  if (session.source !== "import") return false;
  const p = session.provenance;
  const logSource =
    p && typeof p === "object"
      ? (p as Record<string, unknown>).weightLogSource
      : undefined;
  return (
    typeof logSource === "string" &&
    (PATIENT_CORRECTABLE_WEIGHT_LOG_SOURCES as readonly string[]).includes(
      logSource,
    )
  );
}

// TES-1015 — the calendar day a weight belongs to, in the tenant zone. A
// weight log is keyed on that day (`loggedFor`), so matching a measurement
// against a log has to use the same day, not the device's or UTC's.
export const WEIGHT_TIME_ZONE = "Europe/Stockholm";
export function weightDayOf(iso: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: WEIGHT_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(iso));
    const get = (type: string) => parts.find((p) => p.type === type)?.value;
    const day = `${get("year")}-${get("month")}-${get("day")}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(day)) return day;
  } catch {
    // No time-zone data in this runtime: fall through to the UTC day.
  }
  return new Date(iso).toISOString().slice(0, 10);
}

/**
 * TES-1015 — when the reading behind a weight log was taken, comparable with
 * a measurement's `measuredAt`. A log stores only its day and when the row
 * was written. For a log written on its own day (a Home log) the write time
 * is the reading; otherwise (a web lead linked later, a row rewritten by a
 * correction) midday of its day stands in.
 */
export function weightLogMeasuredAt(log: {
  loggedFor: string;
  loggedAt: string;
}): string {
  return weightDayOf(log.loggedAt) === log.loggedFor
    ? log.loggedAt
    : `${log.loggedFor}T10:00:00.000Z`;
}

export const measurementSessionSchema = z.object({
  id: z.string().uuid(),
  measuredAt: z.string(),
  source: measurementSourceSchema,
  sourceSurface: z.string().nullable(),
  current: z.boolean(),
  supersedesId: z.string().nullable(),
  measurements: z.array(measurementSchema),
});
export type MeasurementSession = z.infer<typeof measurementSessionSchema>;

// The 7-day rule (`forms_using_7_day_suggestion`): F-02/F-04/F-05/F-06 offer the
// patient a recent value to confirm instead of re-entering it. A missing or
// stale value NEVER blocks — the form just asks normally, which is why this
// returns a suggestion rather than a requirement.
export const MEASUREMENT_SUGGESTION_WINDOW_DAYS = 7;

export const measurementSuggestionSchema = z.object({
  type: measurementTypeSchema,
  // null when there is no value at all, or the newest is older than the window.
  suggestion: measurementSchema.nullable(),
  // Age of the suggested value in whole days, for "uppmätt för 3 dagar sedan".
  ageDays: z.number().int().nullable(),
});
export type MeasurementSuggestion = z.infer<typeof measurementSuggestionSchema>;

// TES-218 — goal-achievement celebration. The patient sets a goal weight; the
// app celebrates reaching it and surfaces the progress from baseline.
export const setGoalWeightSchema = z.object({
  // Realistic bound; null clears the goal.
  kg: z.number().min(20).max(500).nullable(),
});
export type SetGoalWeight = z.infer<typeof setGoalWeightSchema>;

// TES-649 — the CAR-16 log bodies, parsed at the boundary. Types only: the
// plausible ranges and the `loggedFor` date format are checked by
// WeightService, which answers with its own error codes.
export const logWeightSchema = z.object({
  kg: z.number(),
  loggedFor: z.string().optional(),
});
export type LogWeightRequest = z.infer<typeof logWeightSchema>;

export const logMeasurementSchema = z.object({
  waistCm: z.number().nullable().optional(),
  hipCm: z.number().nullable().optional(),
  loggedFor: z.string().optional(),
});
export type LogMeasurementRequest = z.infer<typeof logMeasurementSchema>;

// TES-863 (Ö-229) — the CLINICIAN's version of the same write.
//
// Emelie 2026-09-23, translated: Kirill must be able to correct or set the
// goal weight in admin; the change requires a reason and is audit-logged with
// its before and after values; the goal updates in the app and the patient is
// told that the doctor has adjusted it.
//
// The reason is REQUIRED here and absent from the patient's own schema above,
// and that asymmetry is the point: she owes nobody an explanation for her own
// goal, and he is changing something that is hers. `.min(1)` after the trim so
// a field of spaces cannot pass for one.
export const adjustGoalWeightSchema = setGoalWeightSchema.extend({
  reason: z.string().trim().min(1).max(2000),
});
export type AdjustGoalWeight = z.infer<typeof adjustGoalWeightSchema>;

export const goalProgressSchema = z.object({
  goalWeightKg: z.number().nullable(),
  // The most recent logged weight, or null if none logged.
  currentKg: z.number().nullable(),
  // The treatment baseline weight, or null if not captured.
  baselineKg: z.number().nullable(),
  // kg lost from baseline → current (>= 0; null when either is missing).
  lostKg: z.number().nullable(),
  // True once a goal is set AND the latest weight is at or below it.
  reachedGoal: z.boolean(),
});
export type GoalProgress = z.infer<typeof goalProgressSchema>;

// Health §7.4 (decision 2026-08-31) — "Din vård" (health/care).
//
// "Byggs just nu" is FORBIDDEN patient copy: it describes product development
// and must not be used as a care status. Every status here must be derivable
// from the patient's actual case state.
//
// The hard rule, translated: no status may claim that the doctor is reviewing
// or preparing a decision before the patient's care flow has reached that
// step.

export const careDecisionStateSchema = z.enum([
  // No decision yet — and no claim that anyone is working on one.
  "none",
  // An actual doctor review in progress.
  "under_review",
  // "Läkaren behöver mer information". NEVER EMITTED YET: the state machine
  // has a way back from doctor_review, but only for RENEWALS — the first-time
  // flow goes pending_assessment → active | rejected with no in-between state.
  // The key is in the contract so the client can be rendered complete, but
  // the server must not invent the state before it exists.
  "more_information_required",
  "published",
]);
export type CareDecisionState = z.infer<typeof careDecisionStateSchema>;

// TES-561 (CP-09 §"Kanonisk copy — Din vård: Behandlingsbeslut"): the page
// shows the doctor's ACTUAL structured decision, never one inferred from a
// general patient status. The outcome is the same mapping meeting-workflow
// uses for the treatment_decision_* flow events — labs first ("komplettering",
// supplementation) wins over proceed/no-proceed, because with labs pending
// there is no final decision yet.
export const treatmentDecisionOutcomeSchema = z.enum([
  "approved",
  "not_approved",
  "more_information_required",
]);
export type TreatmentDecisionOutcome = z.infer<
  typeof treatmentDecisionOutcomeSchema
>;

export function deriveTreatmentDecisionOutcome(decision: {
  labRequired: boolean | null;
  treatmentProceed: boolean | null;
}): TreatmentDecisionOutcome {
  if (decision.labRequired) return "more_information_required";
  return decision.treatmentProceed ? "approved" : "not_approved";
}

// TES-1302 (TES-1299 §1 and §4) — what the approved decision view may say
// about the next step. Read from the treatment-start state machine
// (loadTreatmentStart), never from the cycle or the link alone:
//   - awaiting_activation: approved, subscription not verified. The view says
//     "Nästa steg: aktivera ditt abonnemang" with the masked address.
//   - active: the subscription is verified, however old the decision. The view
//     never shows activation copy and leads to Din behandling instead.
//   - null: no approved decision (declined, completion, none), or a released
//     treatment whose subscription has since lapsed. Neither copy applies.
export const careActivationModeSchema = z.enum([
  "awaiting_activation",
  "active",
]);
export type CareActivationMode = z.infer<typeof careActivationModeSchema>;

export const careActivationSchema = z.object({
  mode: careActivationModeSchema.nullable(),
  // Her verified address, masked ("m•••@gmail.com"). Only while awaiting
  // activation, and null without a verified address.
  maskedEmail: z.string().nullable(),
  // TES-1471 (Ö-494) — only while awaiting activation: "Läkarens kommentar
  // till behandlingsbeslutet", the same notice Din behandling shows. Null
  // when the approval had no text. Optional so an older API still parses.
  comment: treatmentCommentSchema.nullable().optional(),
});
export type CareActivation = z.infer<typeof careActivationSchema>;

// TES-1302 — POST …/care-summary/decision-viewed. `viewed` is false only when
// there is no published decision to have seen.
export const treatmentDecisionViewedResultSchema = z.object({
  viewed: z.boolean(),
});
export type TreatmentDecisionViewedResult = z.infer<
  typeof treatmentDecisionViewedResultSchema
>;

export const careSummarySchema = z.object({
  // False before the first step: the whole surface then shows one coherent
  // empty state instead of five empty areas, which would read as "your care
  // has started but has nothing in it".
  started: z.boolean(),
  decision: careDecisionStateSchema,
  // TES-561: the latest documented meeting's decision, null until a doctor
  // has documented one. `decisionDate` is the documentation timestamp.
  decisionOutcome: treatmentDecisionOutcomeSchema.nullable(),
  decisionDate: z.string().nullable(),
  // TES-1086 (TES-1069 point 6) — on a negative decision only: the doctor's
  // patient-friendly motivation and the "Fortsatt vårdväg", verbatim. Null
  // otherwise, and null for a decline published before the admin told the
  // doctor she would read them. Optional so an older API still parses.
  decisionMotivation: z.string().nullable().optional(),
  decisionCarePath: z.string().nullable().optional(),
  // TES-1302 — optional so an older API still parses; app 2.0.9 (TES-1303)
  // renders it.
  activation: careActivationSchema.optional(),
  planPublished: z.boolean(),
  labOrders: z.number().int().nonnegative(),
  // TES-1613 — how far her latest non-cancelled order has come, by the same
  // derivation as the CP-06 checklist (`deriveBloodTestStatus`), so the
  // "Prover och mätningar" card stops saying "Blodprov beställt" after the
  // sample, the result and the doctor's review. Never `awaiting_payment`:
  // the card describes the order, not a case's payment. Null without an
  // order. Optional so an older API still parses; a build before it keeps
  // reading `labOrders`.
  labStatus: bloodTestStatusSchema.nullable().optional(),
  // TES-1380 — her CP-06 case is open (a completion the doctor asked for,
  // also one without any lab order), so Din vård links "Prover och
  // mätningar" to it. Optional so an older API still parses.
  completionActive: z.boolean().optional(),
  // TES-431 (Ö-89) — the doctor has approved and SENT lab feedback: the
  // soft-green banner that used to sit on the "Hälsa" landing now lives as
  // the status of "Din vård ▸ Prover och svar".
  labFeedbackSent: z.boolean(),
  // TES-1347 (Ö-295) — her newest "Läkarens besked om provsvar": the
  // doctor's word on a Webdoc lab result, published as a medical notice
  // (LAB_FEEDBACK_NOTICE_SUBJECT_TYPE). Din vård links it under "Prover och
  // mätningar" and opens the notice by id. Null when there is none.
  // Optional so an older API still parses; a build before it ignores it and
  // reads the notice in its notices list and history.
  labFeedbackNoticeId: z.string().nullable().optional(),
  prescriptions: z.number().int().nonnegative(),
  followUps: z.number().int().nonnegative(),
});
export type CareSummary = z.infer<typeof careSummarySchema>;

/**
 * Has the patient's care flow started?
 *
 * `lead_started` is the only state before the first step. `paused` and
 * `ended` have ordinal -1 but are OVERLAYS reached only after the flow has
 * started — a comparison on ordinal would wrongly have treated them as "not
 * started".
 */
export function hasStartedCare(journeyState: JourneyState): boolean {
  return journeyState !== "lead_started";
}

/**
 * The treatment decision's status, derived from the real case state.
 *
 * `under_review` requires a cycle that actually sits with the doctor.
 * Deriving it from journeyState alone would have claimed the doctor is
 * reviewing as soon as the patient passed a step, which is exactly what the
 * decision forbids.
 */
export function deriveCareDecisionState(input: {
  journeyState: JourneyState;
  cycleState: PrescriptionCycleState | null;
}): CareDecisionState {
  const { journeyState, cycleState } = input;
  if (
    cycleState === "approved" ||
    cycleState === "active" ||
    cycleState === "expiring_soon" ||
    cycleState === "renewal_pending" ||
    JOURNEY_STATE_ORDINAL[journeyState] >=
      JOURNEY_STATE_ORDINAL.treatment_decision_ready
  ) {
    return "published";
  }
  if (cycleState === "doctor_review") return "under_review";
  return "none";
}
